import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
  PieChart,
  Pie,
} from 'recharts';
import {
  C,
  TIP_COLORS,
  cardStyle,
  SecTitle,
  BLabel,
  abreviar,
  pct,
  type FilterKey,
} from './dashboard-utils';

interface AggDir {
  name: string;
  value: number;
}

interface AggGrp {
  name: string;
  value: number;
  tip: string;
}

interface TipDatum {
  name: string;
  value: number;
  fill: string;
}

interface DashboardChartsProps {
  aggDir: AggDir[];
  aggGrp: AggGrp[];
  tipData: TipDatum[];
  mLen: number;
  tog: (k: FilterKey, v: string) => void;
  dim: (k: FilterKey, v: string) => React.CSSProperties;
}

export function DashboardCharts({ aggDir, aggGrp, tipData, mLen, tog, dim }: DashboardChartsProps) {
  return (
    <>
      {/* Direccion Responsable */}
      <div style={{ ...cardStyle, borderTop: `2px solid ${C.tit}` }}>
        <SecTitle>Direccion responsable</SecTitle>
        <div style={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={aggDir}
              layout="vertical"
              margin={{ left: 180, right: 20, top: 8, bottom: 4 }}
            >
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                width={180}
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                interval={0}
                tickFormatter={(v: string) => abreviar(v, 50)}
              />
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif" }} />
              <Bar
                dataKey="value"
                label={<BLabel />}
                radius={[0, 3, 3, 0]}
                barSize={14}
                onClick={(_: unknown, idx: number) => {
                  const item = aggDir[idx];
                  if (item) tog('direccion', item.name);
                }}
                style={{ cursor: 'pointer' }}
              >
                {aggDir.map((entry, i) => (
                  <Cell key={i} fill={C.a1} style={dim('direccion', entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Grupo de Procesos */}
      <div style={{ ...cardStyle, borderTop: `2px solid ${C.tit}` }}>
        <SecTitle>Grupo de procesos</SecTitle>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <ResponsiveContainer width="100%" height={Math.max(aggGrp.length * 16, 100)}>
            <BarChart
              data={aggGrp}
              layout="vertical"
              margin={{ left: 190, right: 20, top: 8, bottom: 4 }}
            >
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                width={190}
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                interval={0}
                tickFormatter={(v: string) => abreviar(v, 50)}
              />
              <Tooltip contentStyle={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif" }} />
              <Bar
                dataKey="value"
                label={<BLabel />}
                radius={[0, 3, 3, 0]}
                barSize={12}
                onClick={(_: unknown, idx: number) => {
                  const item = aggGrp[idx];
                  if (item) tog('grupoNombre', item.name);
                }}
                style={{ cursor: 'pointer' }}
              >
                {aggGrp.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={TIP_COLORS[entry.tip] ?? C.a1}
                    style={dim('grupoNombre', entry.name)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tipologia donut */}
      <div style={{ ...cardStyle, borderTop: `2px solid ${C.tit}` }}>
        <SecTitle>Tipologia</SecTitle>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <div style={{ flex: '0 0 130px', height: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={tipData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={60}
                  paddingAngle={2}
                  label={({ name, value }: { name?: string; value?: number }) =>
                    `${name ?? ''}: ${value ?? 0}`
                  }
                  onClick={(_: unknown, idx: number) => {
                    const item = tipData[idx];
                    if (item) tog('tipologia', item.name);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {tipData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} style={dim('tipologia', entry.name)} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {tipData.map((entry) => (
              <span
                key={entry.name}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 10,
                  color: C.go,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: entry.fill,
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{ fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: C.a1 }}
                >
                  {entry.value}
                </span>
                {entry.name === 'PE'
                  ? 'Estrategicos'
                  : entry.name === 'PO'
                    ? 'Operativos'
                    : 'Soporte'}
                <span style={{ fontSize: 8, color: C.go }}>({pct(entry.value, mLen)}%)</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
